import React, { useState, useMemo } from 'react';

const AtomicTestViewer = ({ atomicTests, actions = [] }) => {
  const [expandedTactics, setExpandedTactics] = useState({});
  const [expandedTechniques, setExpandedTechniques] = useState({});
  const [expandedTests, setExpandedTests] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [filterPlatform, setFilterPlatform] = useState('all');
  const [filterUsedOnly, setFilterUsedOnly] = useState(false);

  // Get techniques that have been used in actions
  const usedTechniqueIds = useMemo(() => {
    return new Set(
      actions
        .filter(a => a.mitreAttackTechnique)
        .map(a => a.mitreAttackTechnique.id)
    );
  }, [actions]);

  // Organize tests by tactic and technique
  const organizedTests = useMemo(() => {
    const tacticGroups = {};
    
    atomicTests.forEach(test => {
      // Apply filters
      if (filterPlatform !== 'all' && !test.platforms.includes(filterPlatform)) {
        return;
      }
      
      if (filterUsedOnly && !usedTechniqueIds.has(test.techniqueId)) {
        return;
      }
      
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesTest = 
          test.techniqueId.toLowerCase().includes(query) ||
          test.name.toLowerCase().includes(query) ||
          test.description.toLowerCase().includes(query) ||
          test.atomicTests?.some(at => 
            at.name.toLowerCase().includes(query) ||
            at.description.toLowerCase().includes(query)
          );
        
        if (!matchesTest) return;
      }
      
      // Extract tactic from technique name or use a default
      const tactic = test.attackTechnique?.split(' - ')[0] || 'Uncategorized';
      
      if (!tacticGroups[tactic]) {
        tacticGroups[tactic] = {};
      }
      
      if (!tacticGroups[tactic][test.techniqueId]) {
        tacticGroups[tactic][test.techniqueId] = {
          techniqueId: test.techniqueId,
          techniqueName: test.name,
          tests: []
        };
      }
      
      tacticGroups[tactic][test.techniqueId].tests.push(test);
    });
    
    return tacticGroups;
  }, [atomicTests, searchQuery, filterPlatform, filterUsedOnly, usedTechniqueIds]);

  // Get unique platforms
  const availablePlatforms = useMemo(() => {
    const platforms = new Set();
    atomicTests.forEach(test => {
      test.platforms.forEach(platform => platforms.add(platform));
    });
    return Array.from(platforms).sort();
  }, [atomicTests]);

  // Count statistics
  const stats = useMemo(() => {
    const totalTechniques = new Set(atomicTests.map(t => t.techniqueId)).size;
    const totalTests = atomicTests.reduce((sum, t) => sum + (t.atomicTests?.length || 0), 0);
    const coveredTechniques = [...new Set(atomicTests.map(t => t.techniqueId))]
      .filter(id => usedTechniqueIds.has(id)).length;
    
    return {
      totalTechniques,
      totalTests,
      coveredTechniques,
      coveragePercentage: totalTechniques > 0 
        ? ((coveredTechniques / totalTechniques) * 100).toFixed(1)
        : 0
    };
  }, [atomicTests, usedTechniqueIds]);

  const toggleTactic = (tactic) => {
    setExpandedTactics(prev => ({
      ...prev,
      [tactic]: !prev[tactic]
    }));
  };

  const toggleTechnique = (techniqueId) => {
    setExpandedTechniques(prev => ({
      ...prev,
      [techniqueId]: !prev[techniqueId]
    }));
  };

  const toggleTest = (testId) => {
    setExpandedTests(prev => ({
      ...prev,
      [testId]: !prev[testId]
    }));
  };

  const expandAll = () => {
    const allTactics = {};
    const allTechniques = {};
    const allTests = {};
    
    Object.keys(organizedTests).forEach(tactic => {
      allTactics[tactic] = true;
      Object.keys(organizedTests[tactic]).forEach(techniqueId => {
        allTechniques[techniqueId] = true;
        organizedTests[tactic][techniqueId].tests.forEach(test => {
          allTests[test._id] = true;
        });
      });
    });
    
    setExpandedTactics(allTactics);
    setExpandedTechniques(allTechniques);
    setExpandedTests(allTests);
  };

  const collapseAll = () => {
    setExpandedTactics({});
    setExpandedTechniques({});
    setExpandedTests({});
  };

  return (
    <div className="space-y-6">
      {/* Header and Stats */}
      <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
        <h3 className="text-lg font-semibold text-orange-800 mb-2">
          Atomic Red Team Test Coverage
        </h3>
        <div className="grid grid-cols-4 gap-4 text-sm">
          <div>
            <span className="text-gray-600">Total Techniques:</span>
            <span className="ml-2 font-bold text-orange-700">{stats.totalTechniques}</span>
          </div>
          <div>
            <span className="text-gray-600">Total Tests:</span>
            <span className="ml-2 font-bold text-orange-700">{stats.totalTests}</span>
          </div>
          <div>
            <span className="text-gray-600">Used Techniques:</span>
            <span className="ml-2 font-bold text-green-700">{stats.coveredTechniques}</span>
          </div>
          <div>
            <span className="text-gray-600">Coverage:</span>
            <span className="ml-2 font-bold text-green-700">{stats.coveragePercentage}%</span>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="space-y-4">
        <div className="flex gap-4">
          <input
            type="text"
            placeholder="Search techniques or tests..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
          />
          <select
            value={filterPlatform}
            onChange={(e) => setFilterPlatform(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Platforms</option>
            {availablePlatforms.map(platform => (
              <option key={platform} value={platform}>{platform}</option>
            ))}
          </select>
          <label className="flex items-center space-x-2">
            <input
              type="checkbox"
              checked={filterUsedOnly}
              onChange={(e) => setFilterUsedOnly(e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="text-sm text-gray-700">Used Only</span>
          </label>
        </div>
        
        <div className="flex gap-2">
          <button
            onClick={expandAll}
            className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
          >
            Expand All
          </button>
          <button
            onClick={collapseAll}
            className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
          >
            Collapse All
          </button>
        </div>
      </div>

      {/* Hierarchical View */}
      <div className="border border-gray-200 rounded-lg bg-white">
        {Object.keys(organizedTests).length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            No tests found matching your criteria.
          </div>
        ) : (
          <div className="p-4">
            {Object.entries(organizedTests).map(([tactic, techniques]) => (
              <div key={tactic} className="mb-4">
                {/* Tactic Level */}
                <button
                  onClick={() => toggleTactic(tactic)}
                  className="w-full flex items-center justify-between p-3 hover:bg-gray-50 rounded-lg transition-colors"
                >
                  <div className="flex items-center space-x-2">
                    <span className="text-gray-400">
                      {expandedTactics[tactic] ? '▼' : '▶'}
                    </span>
                    <span className="font-semibold text-purple-700 text-lg">
                      {tactic}
                    </span>
                    <span className="text-sm text-gray-500">
                      ({Object.keys(techniques).length} techniques)
                    </span>
                  </div>
                </button>
                
                {expandedTactics[tactic] && (
                  <div className="ml-6 mt-2">
                    {Object.entries(techniques).map(([techniqueId, techniqueData]) => {
                      const isUsed = usedTechniqueIds.has(techniqueId);
                      
                      return (
                        <div key={techniqueId} className="mb-3">
                          {/* Technique Level */}
                          <button
                            onClick={() => toggleTechnique(techniqueId)}
                            className={`w-full flex items-center justify-between p-2 rounded-lg transition-colors ${
                              isUsed ? 'bg-green-50 hover:bg-green-100' : 'hover:bg-gray-50'
                            }`}
                          >
                            <div className="flex items-center space-x-2">
                              <span className="text-gray-400 text-sm">
                                {expandedTechniques[techniqueId] ? '▼' : '▶'}
                              </span>
                              <span className={`font-medium ${isUsed ? 'text-green-700' : 'text-blue-700'}`}>
                                {techniqueId}
                              </span>
                              <span className="text-gray-600">
                                {techniqueData.techniqueName}
                              </span>
                              {isUsed && (
                                <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">
                                  ✓ Used
                                </span>
                              )}
                              <span className="text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded-full">
                                {techniqueData.tests.reduce((sum, t) => sum + (t.atomicTests?.length || 0), 0)} tests
                              </span>
                            </div>
                          </button>
                          
                          {expandedTechniques[techniqueId] && (
                            <div className="ml-6 mt-2">
                              {techniqueData.tests.map(test => (
                                <div key={test._id} className="mb-2">
                                  {/* Test Level */}
                                  <div
                                    className="p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100"
                                    onClick={() => toggleTest(test._id)}
                                  >
                                    <div className="flex items-start justify-between">
                                      <div className="flex-1">
                                        <div className="flex items-center space-x-2">
                                          <span className="text-gray-400 text-sm">
                                            {expandedTests[test._id] ? '▼' : '▶'}
                                          </span>
                                          <h5 className="font-medium text-gray-800">
                                            {test.name}
                                          </h5>
                                        </div>
                                        <p className="text-sm text-gray-600 mt-1 ml-5">
                                          {test.description}
                                        </p>
                                        <div className="flex items-center gap-4 mt-2 ml-5 text-xs">
                                          <span className="text-gray-500">
                                            Platforms: {test.platforms.join(', ')}
                                          </span>
                                          <span className="text-gray-500">
                                            Executor: <span className="font-mono bg-gray-200 px-1 rounded">
                                              {test.executorType}
                                            </span>
                                          </span>
                                        </div>
                                      </div>
                                    </div>
                                    
                                    {/* Individual Atomic Tests */}
                                    {expandedTests[test._id] && test.atomicTests && (
                                      <div className="mt-4 ml-5 space-y-3">
                                        {test.atomicTests.map((atomicTest, index) => (
                                          <div key={index} className="bg-white p-4 rounded-lg border border-gray-200">
                                            <h6 className="font-medium text-gray-800 mb-2">
                                              {atomicTest.name}
                                            </h6>
                                            <p className="text-sm text-gray-600 mb-3">
                                              {atomicTest.description}
                                            </p>
                                            
                                            <div className="space-y-2">
                                              <div className="flex items-center gap-4 text-xs">
                                                <span className="text-gray-500">
                                                  Executor: <span className="font-mono bg-gray-200 px-1 rounded">
                                                    {atomicTest.executor.type}
                                                  </span>
                                                </span>
                                                {atomicTest.executor.elevationRequired && (
                                                  <span className="text-orange-600">
                                                    ! Elevation Required
                                                  </span>
                                                )}
                                                <span className="text-gray-500">
                                                  Platforms: {atomicTest.supportedPlatforms?.join(', ') || 'all'}
                                                </span>
                                              </div>
                                              
                                              {atomicTest.executor.command && (
                                                <details className="mt-2">
                                                  <summary className="cursor-pointer text-sm text-blue-600 hover:text-blue-800">
                                                    View Command
                                                  </summary>
                                                  <pre className="mt-2 p-3 bg-gray-800 text-gray-100 rounded text-xs overflow-x-auto">
                                                    {atomicTest.executor.command}
                                                  </pre>
                                                </details>
                                              )}
                                              
                                              {atomicTest.executor.cleanupCommand && (
                                                <details className="mt-2">
                                                  <summary className="cursor-pointer text-sm text-orange-600 hover:text-orange-800">
                                                    View Cleanup Command
                                                  </summary>
                                                  <pre className="mt-2 p-3 bg-gray-800 text-gray-100 rounded text-xs overflow-x-auto">
                                                    {atomicTest.executor.cleanupCommand}
                                                  </pre>
                                                </details>
                                              )}
                                              
                                              {atomicTest.inputArguments && atomicTest.inputArguments.length > 0 && (
                                                <details className="mt-2">
                                                  <summary className="cursor-pointer text-sm text-purple-600 hover:text-purple-800">
                                                    View Parameters ({atomicTest.inputArguments.length})
                                                  </summary>
                                                  <div className="mt-2 space-y-2">
                                                    {atomicTest.inputArguments.map((arg, idx) => (
                                                      <div key={idx} className="p-2 bg-purple-50 rounded text-xs">
                                                        <div className="font-medium text-purple-800">{arg.name}</div>
                                                        <div className="text-purple-600">{arg.description}</div>
                                                        <div className="text-purple-500">Default: {arg.default || 'None'}</div>
                                                      </div>
                                                    ))}
                                                  </div>
                                                </details>
                                              )}
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AtomicTestViewer;